import { Injectable } from '@angular/core';
import { IFileDataFromDB, IFileItem, ISaveFiles } from './offline.interface';
import { Observable } from 'rxjs';
import { IFile } from '../../typing/file.interface';
import { IndexedDbService } from './IndexedDb.service';
import { MessageService } from '../../services/message.service';

@Injectable({
    providedIn: 'root'
})
export class OfflineFilesService {
    constructor(
        private indexedDb: IndexedDbService,
        private messageService: MessageService
    ) {}

    private generateFileId(orderId: number, fileName: string): string {
        return `${orderId}_${fileName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /** Читаем файл как ArrayBuffer */
    private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (): void => resolve(reader.result as ArrayBuffer);
            reader.onerror = (): void => reject(reader.error);
            reader.readAsArrayBuffer(file);
        });
    }

    /** Сохраняем файлы для определенного заказа */
    public async saveFilesToIndexedDB(files: File[], orderId: number): Promise<void> {
        try {
            const filesWithData = await Promise.all(
                files.map(async file => ({
                    file,
                    arrayBuffer: await this.readFileAsArrayBuffer(file)
                }))
            );

            for (const { file, arrayBuffer } of filesWithData) {
                const fileData = {
                    id: this.generateFileId(orderId, file.name),
                    orderId: orderId,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    data: arrayBuffer,
                };

                await this.indexedDb.add('files', fileData);
            }
        } catch {
            this.messageService.errorMessage('Ошибка сохранения файлов');
        }
    }

    /** Получаем все файлы (или по конкретному orderId) */
    async getOfflineFiles(orderId?: number): Promise<ISaveFiles[]> {
        try {
            const files = orderId
                ? await this.indexedDb.getAllByIndex<IFileDataFromDB>('files', 'orderId', orderId)
                : await this.indexedDb.getAll<IFileDataFromDB>('files');

            const filesWithData: IFileItem[] = files.map(fileData => {
                const file = new File([fileData.data], fileData.name, {
                    type: fileData.type,
                    lastModified: fileData.lastModified
                });

                return {
                    file,
                    metadata: {
                        id: fileData.id.toString(),
                        orderId: fileData.orderId,
                        name: fileData.name,
                        type: fileData.type,
                        size: fileData.size
                    }

                };
            });

            // группировка по orderId
            const groupedResult = filesWithData.reduce((acc: { [key: number]: IFileItem[] }, item) => {
                const id = item.metadata.orderId;
                if (!acc[id]) {
                    acc[id] = [];
                }
                acc[id].push(item);
                return acc;
            }, {});

            return Object.entries(groupedResult).map(([orderId, files]) => ({
                orderId: Number(orderId),
                files: this.createFileList(files)
            }));
        } catch {
            return [];
            this.messageService.errorMessage('Ошибка при получении файлов');
        }
    }

    /** Удаление всех файлов по orderId */
    async deleteFilesByOrderId(orderId: number): Promise<void> {
        const keys = await this.indexedDb.getAllKeysByIndex('files', 'orderId', orderId);

        for (const key of keys) {
            await this.indexedDb.delete('files', key);
        }
    }

    /** Преобразуем массив файлов в FileList */
    private createFileList(files: IFileItem[]): FileList {
        const dataTransfer = new DataTransfer();

        files
            .map(file => {
                const fileNameParts = file.file.name.split('.');
                const extension = fileNameParts.pop();
                const newFileName = `${Date.now()}_${fileNameParts.join('.')}.${extension}`;

                return new File([file.file], newFileName, { type: file.file.type });
            })
            .forEach(modifiedFile => {
                dataTransfer.items.add(modifiedFile);
            });

        return dataTransfer.files;
    }

    fileToBase64(file: File):Observable<Partial<IFile>> {
        /** для преобразования файла в base64 */
        return new Observable((observer) => {
            const reader = new FileReader();

            reader.onload = (): void => {
                const dataUrl = reader.result as string;
                const pureBase64 = dataUrl.split(',')[1];
                const format = file.type.split('/')[1];
                observer.next({
                    buffer: pureBase64,
                    name: file.name,
                    format: format
                });
                observer.complete();
            };

            reader.onerror = (error): void => {
                observer.error(error);
            };
            reader.readAsDataURL(file);
        });

    }
}
